import { formString, needsTenantChoice, Permission } from '@pedigreesmart/core'
import { useState } from 'react'

import logo from './assets/logo-pedigreesmart.png'
import { AnimalDetail } from './animals/AnimalDetail.js'
import { AnimalForm } from './animals/AnimalForm.js'
import { AnimalList } from './animals/AnimalList.js'
import { LitterList } from './animals/LitterList.js'
import { DeclarationForm } from './declarations/DeclarationForm.js'
import { DeclarationList } from './declarations/DeclarationList.js'
import { AppShell, type VoceMenu } from './layout/AppShell.js'
import { MembershipList } from './people/MembershipList.js'
import { PersonDetail } from './people/PersonDetail.js'
import { PersonForm } from './people/PersonForm.js'
import { PersonList } from './people/PersonList.js'
import { AuditLog } from './settings/AuditLog.js'
import { BillingUsage } from './settings/BillingUsage.js'
import { LookupList } from './settings/LookupList.js'
import { TenantSettingsForm } from './settings/TenantSettingsForm.js'
import { TenantUserList } from './settings/TenantUserList.js'
import { useSession, useTerm } from './session/SessionProvider.js'
import './app.css'

export function App() {
  const { context } = useSession()

  if (!context) {
    return <SchermataAccesso />
  }

  // Un profilo iscritto a due associazioni tiene un token per una alla volta:
  // scegliere significa chiedere un token diverso, non applicare un filtro.
  if (needsTenantChoice(context)) {
    return <SceltaAssociazione />
  }

  return <Gestionale />
}

function Gestionale() {
  const { can } = useSession()
  const [sezione, setSezione] = useState('animali')
  // Un id aperto invece di una rotta: il gestionale non ha ancora un router, e
  // introdurlo per una sola schermata sarebbe una struttura in cerca di un uso.
  const [apertoId, setApertoId] = useState<number | null>(null)
  // `'nuovo'` invece di un secondo booleano: creare e modificare sono lo
  // stesso modulo, e due stati separati potrebbero contraddirsi.
  const [modulo, setModulo] = useState<number | 'nuovo' | null>(null)
  const gestisciPersone = can(Permission.personManage)
  const gestisciSoggetti = can(Permission.animalManage)
  // Il `POST /declarations` chiede `declaration.submit` **oppure**
  // `declaration.review`: chi presenta e chi istruisce possono entrambi
  // aprire una pratica. Verificato sulla risorsa, non dedotto dal nome.
  const apreDenunce = can(Permission.declarationSubmit) || can(Permission.declarationReview)
  const animali = useTerm('animal.plural')
  const cucciolate = useTerm('litter')

  /*
   * Le voci nascoste sono una cortesia, non un controllo: ogni permesso è
   * riverificato dal backend a ogni richiesta. Nascondere un pulsante evita a
   * qualcuno di scoprire di non poterlo usare dopo averci cliccato — non
   * impedisce niente a chi volesse aggirarlo.
   */
  const voci: readonly VoceMenu[] = [
    { chiave: 'animali', etichetta: animali, visibile: can(Permission.animalView) || can(Permission.animalViewOwn) },
    { chiave: 'cucciolate', etichetta: cucciolate, visibile: can(Permission.animalView) },
    { chiave: 'persone', etichetta: 'Persone', visibile: can(Permission.personView) },
    { chiave: 'denunce', etichetta: 'Denunce', visibile: can(Permission.declarationView) },
    { chiave: 'tesseramenti', etichetta: 'Tesseramenti', visibile: can(Permission.membershipView) },
    { chiave: 'utenti', etichetta: 'Utenti', visibile: can(Permission.tenantUsers) },
    { chiave: 'associazione', etichetta: 'Associazione', visibile: can(Permission.tenantSettings) },
    { chiave: 'liste', etichetta: 'Liste', visibile: can(Permission.lookupManage) },
    { chiave: 'consumi', etichetta: 'Consumi', visibile: can(Permission.billingView) },
    { chiave: 'registro', etichetta: 'Registro accessi', visibile: can(Permission.auditView) },
  ]

  return (
    <AppShell
      voci={voci}
      attiva={sezione}
      onNaviga={(chiave) => {
        setApertoId(null)
        setModulo(null)
        setSezione(chiave)
      }}
    >
      {sezione === 'animali' && modulo !== null && (
        <AnimalForm
          id={modulo === 'nuovo' ? undefined : modulo}
          onFatto={(id) => {
            setModulo(null)
            setApertoId(id)
          }}
          onAnnulla={() => {
            setModulo(null)
          }}
        />
      )}
      {sezione === 'animali' && modulo === null && apertoId === null && (
        <AnimalList
          onApri={setApertoId}
          onNuovo={
            gestisciSoggetti
              ? () => {
                  setModulo('nuovo')
                }
              : undefined
          }
        />
      )}
      {sezione === 'animali' && modulo === null && apertoId !== null && (
        <AnimalDetail
          id={apertoId}
          onTorna={() => {
            setApertoId(null)
          }}
          onModifica={gestisciSoggetti ? setModulo : undefined}
        />
      )}
      {sezione === 'persone' && modulo !== null && (
        <PersonForm
          id={modulo === 'nuovo' ? undefined : modulo}
          onFatto={(id) => {
            setModulo(null)
            // Dopo il salvataggio si apre la scheda, non si torna all'elenco:
            // chi ha appena inserito una persona di solito le aggiunge
            // subito un tesseramento o un soggetto.
            setApertoId(id)
          }}
          onAnnulla={() => {
            setModulo(null)
          }}
        />
      )}
      {sezione === 'persone' && modulo === null && apertoId === null && (
        <PersonList
          onApri={setApertoId}
          onNuova={
            gestisciPersone
              ? () => {
                  setModulo('nuovo')
                }
              : undefined
          }
        />
      )}
      {sezione === 'persone' && modulo === null && apertoId !== null && (
        <PersonDetail
          id={apertoId}
          onTorna={() => {
            setApertoId(null)
          }}
          onModifica={gestisciPersone ? setModulo : undefined}
        />
      )}
      {sezione === 'utenti' && <TenantUserList />}
      {sezione === 'associazione' && <TenantSettingsForm />}
      {sezione === 'liste' && <LookupList />}
      {sezione === 'consumi' && <BillingUsage />}
      {sezione === 'registro' && <AuditLog />}

      {/* Aprire un nato porta alla sua scheda, che vive nella sezione dei
          soggetti: la cucciolata è un modo di arrivarci, non un secondo posto
          in cui rileggere le stesse cose. */}
      {sezione === 'cucciolate' && (
        <LitterList
          onApriSoggetto={(id) => {
            setSezione('animali')
            setApertoId(id)
          }}
        />
      )}

      {sezione === 'denunce' && modulo !== null && (
        <DeclarationForm
          onFatto={() => {
            setModulo(null)
          }}
          onAnnulla={() => {
            setModulo(null)
          }}
        />
      )}
      {sezione === 'denunce' && modulo === null && (
        <DeclarationList
          onNuova={
            apreDenunce
              ? () => {
                  setModulo('nuovo')
                }
              : undefined
          }
        />
      )}

      {/* I tesseramenti aprono la scheda della persona: gli anni si
          aggiungono lì, e chi cerca un socio vuole vedere anche il resto. */}
      {sezione === 'tesseramenti' && apertoId === null && <MembershipList onApri={setApertoId} />}
      {sezione === 'tesseramenti' && apertoId !== null && (
        <PersonDetail
          id={apertoId}
          onTorna={() => {
            setApertoId(null)
          }}
        />
      )}
    </AppShell>
  )
}

function SceltaAssociazione() {
  const { context, switchTenant, t } = useSession()

  return (
    <main className="centrata">
      <div className="riquadro">
        <h1 className="marchio">
          <img src={logo} alt={t('app.name')} width={400} height={87} />
        </h1>
        <h2>Scegliere l'associazione</h2>
        <ul className="elenco-scelte">
          {context?.tenants.map((tenant) => (
            <li key={tenant.id}>
              <button
                type="button"
                data-variante="secondario"
                onClick={() => {
                  void switchTenant(tenant.id)
                }}
              >
                {tenant.name}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </main>
  )
}

function SchermataAccesso() {
  const { login, t } = useSession()
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  return (
    <main className="centrata">
      <form
        className="riquadro"
        onSubmit={(event) => {
          event.preventDefault()
          const dati = new FormData(event.currentTarget)
          setErrore(null)
          setInCorso(true)

          void login(formString(dati, 'email'), formString(dati, 'password'))
            .catch(() => {
              // Un messaggio solo per «email sconosciuta» e «password
              // sbagliata»: distinguerli direbbe a chi prova quali indirizzi
              // esistono.
              setErrore('Credenziali non valide.')
            })
            .finally(() => {
              setInCorso(false)
            })
        }}
      >
        {/* Il logo porta già la scritta «PedigreeSmart», quindi sta dentro
            l'h1 come immagine: chi vede legge il marchio, chi ascolta sente il
            nome dal testo alternativo. Ripeterlo scritto sotto sarebbe una
            ripetizione per gli uni e un doppione per gli altri. */}
        <h1 className="marchio">
          <img src={logo} alt={t('app.name')} width={400} height={87} />
        </h1>

        {errore && (
          <p role="alert" className="messaggio messaggio--errore">
            {errore}
          </p>
        )}

        <label htmlFor="email">Email</label>
        <input id="email" name="email" type="email" autoComplete="username" required autoFocus />

        <label htmlFor="password">Password</label>
        <input id="password" name="password" type="password" autoComplete="current-password" required />

        <button type="submit" disabled={inCorso}>
          {inCorso ? 'Accesso in corso…' : 'Accedi'}
        </button>
      </form>
    </main>
  )
}
